import { useTranslate } from 'hooks/useTranslate';
import { setModal } from 'modules/_shared/redux/modalActions';
import { FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import sizes from 'constants/sizes';

import isSuperAdmin from 'utils/isSuperAdmin';
import {
  formatCurrencyDecimals,
  formatNumber,
  formatNumberDecimals,
} from 'utils/formats';
import { societyHasTitles } from 'utils/societyHasTitles';

import UpdateShares from 'modules/_shared/components/Modals/UpdateShares';
import DocumentsColumn from 'modules/_shared/components/DocumentsColumn';

import { Document, HoldingClass, Share, SharesTitle } from 'types';
import { State } from 'redux/initialState';
import { getPartnerEncumbrancePercent } from 'modules/partners/utils/getPartnerEncumbrancePercent';
import { getShareEncumbranceNames } from 'modules/partners/utils/getShareEncumbranceNames';
import { useGetShareTitle } from '../hooks/useGetShareTitle';
import EncumbrancesTooltip from './EncumbrancesTooltip';

type PartnerSharesTableRowProps = {
  partnerId: string;
  index: number;
  share: Share;
  sharesTitles: SharesTitle[];
  societySharesTitleDocument?: Document;
  isCoOwner: boolean;
  fetchPartnerData: () => void;
};

const PartnerSharesTableRow: FC<PartnerSharesTableRowProps> = ({
  partnerId,
  index,
  share,
  sharesTitles,
  societySharesTitleDocument,
  isCoOwner,
  fetchPartnerData,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const user = useSelector((state: State) => state?.user);
  const isAdmin = useSelector((state: State) => state.society?.role?.isAdmin);
  const actualSociety = useSelector(
    (state: State) => state?.society?.actualSociety || {}
  );

  const hasTilesEnabled = societyHasTitles(actualSociety);

  const { sharesTitle, titleWatermarkText } = useGetShareTitle({
    share,
    sharesTitles,
  }).data;

  const sharesCount = share?.to - share?.from + 1;
  const shareClass = share?.shareClass as HoldingClass;
  const shareClassName = shareClass?.name || '';
  const nominalValue =
    shareClass?.nominalValue || actualSociety?.nominalValue || 0;

  const encumbrancePercent =
    getPartnerEncumbrancePercent(partnerId, share?.encumbrances || []) || '-';
  const encumbranceNames = getShareEncumbranceNames(share, partnerId, t);

  const handleClickRow = () => {
    if (isAdmin || isSuperAdmin(user)) {
      dispatch(
        setModal(
          <UpdateShares
            share={share}
            society={actualSociety}
            isReadOnly={isCoOwner}
            fetchPartnerData={fetchPartnerData}
            size={sizes.XL}
          />
        )
      );
    }
  };

  return (
    <>
      <tr className="tb-tnx-item cursor-pointer">
        <td
          className="text-left vertical-align-middle"
          onClick={handleClickRow}
        >
          <b>{index}</b>
        </td>

        <td
          className="text-right vertical-align-middle"
          onClick={handleClickRow}
        >
          {sharesCount ? formatNumber(sharesCount) : '-'}
        </td>

        {!isCoOwner && (
          <td
            className="text-right vertical-align-middle"
            onClick={handleClickRow}
          >
            {formatCurrencyDecimals(nominalValue)}
          </td>
        )}

        {isCoOwner && (
          <td className="vertical-align-middle" onClick={handleClickRow}>
            {encumbranceNames || '-'}
          </td>
        )}

        <td
          className="text-left text-ellipsis vertical-align-middle"
          onClick={handleClickRow}
        >
          {shareClassName}
        </td>

        <td
          className="text-right vertical-align-middle"
          onClick={handleClickRow}
        >
          {share?.from ? formatNumber(share.from) : '-'}
        </td>

        <td
          className="text-right vertical-align-middle"
          onClick={handleClickRow}
        >
          {share?.to ? formatNumber(share.to) : '-'}
        </td>

        {isAdmin && !isCoOwner && (
          <td
            className="text-center vertical-align-middle"
            onClick={handleClickRow}
          >
            <EncumbrancesTooltip
              shareId={share?._id}
              encumbrances={share?.encumbrances || []}
            />
          </td>
        )}

        {isAdmin &&
          !isCoOwner &&
          hasTilesEnabled &&
          (sharesTitle ? (
            <DocumentsColumn
              index={index}
              documentsCount={1}
              documents={
                societySharesTitleDocument ? [societySharesTitleDocument] : []
              }
              hideOnMobile={false}
              sources={{
                society: actualSociety?._id,
                sharesTitle: sharesTitle?._id,
              }}
              templateWatermarkText={titleWatermarkText}
            />
          ) : (
            <td className="text-center vertical-align-middle">
              <span>-</span>
            </td>
          ))}
      </tr>
    </>
  );
};
export default PartnerSharesTableRow;
